import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Trophy, MapPin, Calendar, Radio, GitBranch } from "lucide-react";
import { Link } from "react-router-dom";
import ShareMenu from "@/components/ShareMenu";
import PsiMoversBoard from "@/components/PsiMoversBoard";
import { LotteryBoard, useLottery } from "@/components/TdLotteryPanel";
import SponsorSlide from "@/components/SponsorSlide";
import WatchLiveButton from "@/components/WatchLiveButton";
import ServeBox from "@/components/ServeBox";
import CourtTimer from "@/components/CourtTimer";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { canSeePage } from "@/lib/pages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PB_LOGO = "/brand/pb-logo.png";

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const iv = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(iv); }, []);
  return <span className="font-mono tabular-nums">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>;
};

const evLabel = (m) => [m?.event_type, m?.age_bracket].filter(Boolean).join(" · ");

function CourtCard({ item }) {
  const live = !!item.playing;
  return (
    <div data-testid={`court-card-${item.court}`}
      className={`relative border p-4 flex flex-col min-h-[170px] transition-colors ${
        live ? "border-[#A3E635] bg-[#A3E635]/5 shadow-[0_0_24px_rgba(163,230,53,0.12)]" : "border-zinc-800 bg-zinc-950"}`}>
      {item.medal && (
        <div data-testid={`medal-label-${item.court}`}
          className={`text-[9px] font-black uppercase tracking-[0.25em] mb-1.5 ${item.medal === "championship" ? "text-yellow-400" : "text-[#22D3EE]"}`}>
          {item.medal === "championship" ? "🏆 Championship Court" : "⭐ Center Court"}
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <span className={`font-display font-black text-3xl leading-none ${live ? "text-[#A3E635]" : "text-zinc-700"}`}>
          {item.court}
        </span>
        {live ? (
          <span className="flex items-center gap-2">
            <CourtTimer since={item.playing.court_assigned_at} testid={`court-timer-${item.court}`} />
            <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#A3E635]">
              <span className="w-2 h-2 rounded-full bg-[#A3E635] animate-pulse" /> Live
            </span>
          </span>
        ) : (
          <span className="text-[10px] font-black uppercase tracking-widest text-zinc-700">Open</span>
        )}
      </div>
      {live ? (
        <div className="flex-1">
          <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{item.playing.label}{evLabel(item.playing) ? ` · ${evLabel(item.playing)}` : ""}</div>
          <ServeBox serving={item.playing.first_serve === "a"} testid={`court-side-a-${item.court}`} className="flex items-center justify-between gap-2">
            <div className="font-display font-bold uppercase text-lg leading-tight text-white">{item.playing.side_a}</div>
            {item.playing.live_score && <span className="font-mono font-black text-2xl text-[#A3E635] tabular-nums" data-testid={`court-live-a-${item.court}`}>{item.playing.live_score.a}</span>}
          </ServeBox>
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 my-1 flex items-center gap-2">vs{item.playing.live_score && <span className="text-red-400 font-black flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />live · G{item.playing.live_score.game}</span>}</div>
          <ServeBox serving={item.playing.first_serve === "b"} testid={`court-side-b-${item.court}`} className="flex items-center justify-between gap-2">
            <div className="font-display font-bold uppercase text-lg leading-tight text-white">{item.playing.side_b}</div>
            {item.playing.live_score && <span className="font-mono font-black text-2xl text-[#A3E635] tabular-nums" data-testid={`court-live-b-${item.court}`}>{item.playing.live_score.b}</span>}
          </ServeBox>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-zinc-800 font-display font-black uppercase text-sm">Court Open</div>
      )}
      {item.on_deck && (
        <div className="mt-2 pt-2 border-t border-[#22D3EE]/30" data-testid={`on-deck-${item.court}`}>
          <div className="text-[9px] font-black uppercase tracking-[0.2em] text-[#22D3EE] mb-0.5">On Deck 🔜</div>
          <div className="text-xs text-zinc-300 leading-snug">{item.on_deck.side_a} <span className="text-zinc-600">vs</span> {item.on_deck.side_b}</div>
        </div>
      )}
    </div>
  );
}

function Picker() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => {
    axios.get(`${API}/court-board/tournaments`).then(({ data }) => setList(data)).catch(() => setList([]));
  }, []);
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="flex items-center gap-4 mb-6">
        <img src={PB_LOGO} alt="Pickle Bullpen" className="w-16 h-16 object-contain" />
        <div>
          <h1 className="font-display font-black uppercase text-4xl leading-none">Live Court Board</h1>
          <p className="text-zinc-500 text-sm mt-1">Pick a tournament to put on the big screen.</p>
        </div>
      </div>
      <div className="mb-8 border border-zinc-800 bg-zinc-950 px-4 py-3 flex items-center justify-between gap-3" data-testid="board-share-row">
        <span className="text-xs text-zinc-500">Send friends & family the live board — no account needed</span>
        <ShareMenu url={`${window.location.origin}/court-board`}
          text="🏓 Follow the live pickleball action — courts, matchups and scores updating in real time on the Pickle Bullpen Court Board!"
          testId="board-share" label="Share" />
      </div>
      {list === null && <div className="text-zinc-500">Loading tournaments…</div>}
      {list?.length === 0 && <div className="text-zinc-500 border border-zinc-800 p-6">No live or upcoming tournaments right now.</div>}
      <div className="space-y-3">
        {list?.map((t) => (
          <button key={t.id} data-testid={`board-pick-${t.id}`} onClick={() => navigate(`/court-board/${t.id}`)}
            className="w-full text-left border border-zinc-800 bg-zinc-950 p-5 hover:border-[#A3E635] transition-colors flex items-center justify-between gap-4">
            <div>
              <div className="font-display font-black uppercase text-xl">{t.name}</div>
              <div className="text-xs text-zinc-500 mt-1 flex gap-4">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{t.date}</span>
                {t.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.location}</span>}
                <span>{t.courts.length} courts · {t.entry_count} entries</span>
              </div>
            </div>
            <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 border ${
              t.status === "in_progress" ? "text-[#22D3EE] border-[#22D3EE]/50" : "text-[#A3E635] border-[#A3E635]/50"}`}>
              {t.status === "in_progress" ? "Live" : "Open"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CourtBoard() {
  const { tid } = useParams();
  const [data, setData] = useState(null);
  const [extras, setExtras] = useState(null);
  const [movers, setMovers] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const { data: lottery } = useLottery(tid);
  const contentCtx = useContent();
  const allowed = canSeePage(user, contentCtx?.content?.["access.court-board"]);

  const load = useCallback(async () => {
    if (!tid) return;
    try {
      const { data } = await axios.get(`${API}/tournaments/${tid}/court-board`);
      setData(data); setError(null);
      axios.get(`${API}/public/tournaments/${tid}/psi-movers`).then((r) => setMovers(r.data)).catch(() => {});
    } catch { setError("Tournament not found"); }
  }, [tid]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (!tid) return;
    const loadExtras = () => axios.get(`${API}/tournaments/${tid}/court-board-extras`)
      .then(({ data }) => setExtras(data)).catch(() => {});
    loadExtras();
    const iv = setInterval(loadExtras, 30000);
    return () => clearInterval(iv);
  }, [tid]);

  if (!tid) return <div className="min-h-screen bg-black text-white"><Picker /></div>;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="court-board-page">
      {error && <div className="p-16 text-center text-zinc-500 font-display uppercase text-2xl">{error}</div>}
      {!error && !data && <div className="p-16 text-center text-zinc-500">Loading court board…</div>}
      {data && (
        <>
          <header className="border-b border-zinc-800 px-6 py-4 flex flex-wrap items-center justify-between gap-4 sticky top-0 bg-black/95 backdrop-blur z-10">
            <div className="flex items-center gap-4">
              <img src={data.tournament.logo || PB_LOGO} alt="" className="w-12 h-12 object-contain" data-testid="board-event-logo" />
              <div>
                <h1 className="font-display font-black uppercase text-2xl md:text-3xl leading-none" data-testid="board-title">{data.tournament.name}</h1>
                <div className="text-xs text-zinc-500 flex gap-4 mt-1">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{data.tournament.date}</span>
                  {data.tournament.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{data.tournament.location}</span>}
                  <span className="flex items-center gap-1"><Trophy className="w-3 h-3" />{data.stats.matches_done}/{data.stats.matches_total} matches played</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <WatchLiveButton />
              <Link to={`/brackets/${tid}`} data-testid="view-brackets-btn"
                className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-2 border border-[#22D3EE]/50 text-[#22D3EE] hover:bg-[#22D3EE] hover:text-black transition-colors">
                <GitBranch className="w-3.5 h-3.5" /> View Brackets
              </Link>
              <ShareMenu url={`${window.location.origin}/court-board/${tid}`}
                text={`🏓 Follow ${data.tournament.name} LIVE — courts, matchups and scores in real time!`}
                testId="board-detail-share" label="Share" />
              <span className="hidden sm:flex items-center gap-2 text-[#A3E635] text-xs font-black uppercase tracking-widest">
                <Radio className="w-4 h-4 animate-pulse" /> Live · updates every 10s
              </span>
              <span className="font-display font-black text-3xl text-white"><Clock /></span>
            </div>
          </header>

          <main className="p-6">
            {data.board.length === 0 ? (
              <div className="text-center text-zinc-600 py-20 font-display uppercase text-xl">
                No courts assigned yet — the Tournament Director sets courts from the bracket page.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4" data-testid="court-grid">
                {data.board.map((item) => <CourtCard key={item.court} item={item} />)}
              </div>
            )}

            <div className="mt-8" data-testid="board-psi-movers-wrap">
              <PsiMoversBoard data={movers} live={!movers?.final} title={movers?.final ? "Tournament Top 3 PSI Movers" : "Top 3 PSI Movers"} testid="board-psi-movers" />
            </div>
            {lottery?.board?.length > 0 && (
              <div className="mt-8" data-testid="board-lottery-wrap">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="font-display font-black uppercase text-lg text-white">PBR Lottery Board</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Your number = your PBR points rank at this event</div>
                </div>
                <LotteryBoard board={lottery.board} highlight={lottery.draws?.[0]?.number} testid="board-lottery" />
                {lottery.draws?.length > 0 && (
                  <div className="mt-2 text-xs text-zinc-400" data-testid="board-lottery-last">
                    Last draw: <span className="font-black text-pb-red">#{lottery.draws[0].number}</span>
                    {lottery.draws[0].winner ? <> — <span className="text-white font-bold">{lottery.draws[0].winner.name}</span></> : " — no player, roll again"}
                    {lottery.draws[0].prize ? ` · ${lottery.draws[0].prize}` : ""}
                  </div>
                )}
              </div>
            )}

            {data.up_next.length > 0 && (
              <div className="mt-8" data-testid="up-next-strip">
                <div className="text-xs font-black uppercase tracking-[0.3em] text-[#22D3EE] mb-3">Up Next — Waiting For A Court</div>
                <div className="flex flex-wrap gap-3">
                  {data.up_next.map((m, i) => (
                    <div key={i} className="border border-zinc-800 bg-zinc-950 px-4 py-2.5">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{m.label}{evLabel(m) ? ` · ${evLabel(m)}` : ""}</div>
                      <div className="text-sm font-semibold">{m.side_a} <span className="text-zinc-600">vs</span> {m.side_b}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {extras?.groups?.length > 0 && (
              <div className="mt-12" data-testid="board-scores-standings">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="text-xs font-black uppercase tracking-[0.3em] text-[#A3E635]">Approved Scores & Standings</div>
                  <Link to={`/brackets/${tid}`} data-testid="view-brackets-bottom"
                    className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-2 border border-[#22D3EE]/50 text-[#22D3EE] hover:bg-[#22D3EE] hover:text-black transition-colors">
                    <GitBranch className="w-3.5 h-3.5" /> Searchable Bracket View
                  </Link>
                </div>
                <div className="grid lg:grid-cols-2 gap-5">
                  {extras.groups.map((g) => (
                    <div key={g.label} className="border border-zinc-800 bg-zinc-950 p-4" data-testid={`board-group-${g.label}`}>
                      <div className="font-display font-black uppercase text-lg text-white mb-3">{g.label}</div>
                      {g.standings.length > 0 && (
                        <table className="w-full text-sm mb-4">
                          <thead>
                            <tr className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 text-left">
                              <th className="pb-1.5 pr-2">#</th><th className="pb-1.5">Team</th>
                              <th className="pb-1.5 text-center">W</th><th className="pb-1.5 text-center">L</th>
                              <th className="pb-1.5 text-right">PF/PA</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.standings.map((r) => (
                              <tr key={r.rank} className="border-t border-zinc-900">
                                <td className={`py-1.5 pr-2 font-mono ${r.rank === 1 ? "text-[#A3E635] font-bold" : "text-zinc-500"}`}>{r.rank}</td>
                                <td className="py-1.5 font-semibold">{r.team}</td>
                                <td className="py-1.5 text-center text-green-400">{r.wins}</td>
                                <td className="py-1.5 text-center text-red-400">{r.losses}</td>
                                <td className="py-1.5 text-right font-mono text-zinc-500">{r.pf}/{r.pa}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {g.results.length > 0 ? (
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 mb-1.5">Approved Scores</div>
                          <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                            {g.results.map((r, i) => (
                              <div key={i} className="flex flex-wrap items-center gap-x-2 text-xs border-t border-zinc-900 pt-1">
                                <span className="text-zinc-600 uppercase text-[9px] tracking-widest">{r.round}</span>
                                <span className="font-semibold text-white">{r.winner}</span>
                                <span className="text-[9px] uppercase text-[#A3E635] font-bold">def.</span>
                                <span className="text-zinc-400">{r.loser}</span>
                                <span className="font-mono text-[#A3E635]">{r.score}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-zinc-600">No approved scores yet — first results land here.</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>

          <SponsorSlide sponsors={data.tournament.sponsors} testid="board-sponsors" />
          <footer className="px-6 py-3 border-t border-zinc-800 text-[11px] text-zinc-600 flex justify-between">
            <span>Pickle Bullpen · Live Court Board</span>
            <span>{data.stats.pending_scores > 0 ? `${data.stats.pending_scores} score${data.stats.pending_scores === 1 ? "" : "s"} awaiting TD approval` : "All scores up to date"}</span>
          </footer>
        </>
      )}
    </div>
  );
}
